import React, { useLayoutEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import Content from '../../../layout/Content';
import Loader from '../../../common/Loader';
import ResourceProperty from '../../../common/resource/ResourceProperty';
import EnumStatus from '../../../common/EnumStatus';
import DateCell from '../../../common/cells/DateCell';
import LinkToModuleResource from '../../../common/resource/LinkToModuleResource';
import NoDataResource from '../../../common/resource/NoDataResource';
import {
  breadcrumbsPropTypes,
  matchPropTypes,
} from '../../../common/resource/proptypes/CommonPropTypes';
import { ITINERARY_COUNTER_REPORT_PATH } from '../../../../config/paths';
import { ITINERARY_COUNTER_REPORT_STATUS } from '../../../../config/constants';
import { DATE_TIME_FORMAT } from '../../../../config/locale';
import {
  getItineraryCounterReport,
  clearItineraryCounterReport,
} from '../../../../actions';

const FILTER_LABELS = {
  sourceLocationId: 'Origen',
  destinationLocationId: 'Destino',
  fromDateString: 'Fecha Inicial',
  toDateString: 'Fecha Final',
  serviceTypeIds: 'Tipos de Servicio',
  directionType: 'Dirección',
  showNorteChico: 'Norte Chico',
  driverId: 'Conductor',
  cabinCrewId: 'Tripulante de Cabina',
  activeOnly: 'Solo activos',
};

const formatFilterValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (Array.isArray(value)) return value.length ? value.join(', ') : null;
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  return String(value);
};

const ItineraryCounterReport = ({
  breadcrumbs,
  match: {
    params: { id: itineraryCounterReportId },
  },
  itineraryCounterReport,
  loading,
  dispatchGetItineraryCounterReport,
  dispatchClearItineraryCounterReport,
}) => {
  useLayoutEffect(() => {
    dispatchGetItineraryCounterReport({ itineraryCounterReportId });
    return () => dispatchClearItineraryCounterReport();
  }, []);

  let content;

  if (loading) {
    content = <Loader />;
  } else if (itineraryCounterReport.isEmpty()) {
    content = <NoDataResource returnPage={ITINERARY_COUNTER_REPORT_PATH} />;
  } else {
    const report = itineraryCounterReport.toJS();
    const filters = report.filters || {};
    const filterRows = Object.keys(FILTER_LABELS)
      .map((key) => ({ key, value: formatFilterValue(filters[key]) }))
      .filter((row) => row.value !== null);

    content = (
      <>
        <ResourceProperty label="ID:">{report.id}</ResourceProperty>
        <ResourceProperty label="Estado:">
          <EnumStatus
            enumObject={ITINERARY_COUNTER_REPORT_STATUS}
            enumValue={report.status}
          />
        </ResourceProperty>
        <ResourceProperty label="Solicitado:">
          <DateCell value={report.requestedAt} format={DATE_TIME_FORMAT} />
        </ResourceProperty>
        <ResourceProperty label="Completado:">
          <DateCell value={report.completedAt} format={DATE_TIME_FORMAT} />
        </ResourceProperty>
        <ResourceProperty label="Generado por:">
          {report.createdBy || 'Desconocido'}
        </ResourceProperty>
        <ResourceProperty label="Notas:">
          {report.notes || '-'}
        </ResourceProperty>
        <h3>Filtros solicitados</h3>
        {filterRows.map((row) => (
          <ResourceProperty key={row.key} label={`${FILTER_LABELS[row.key]}:`}>
            {row.value}
          </ResourceProperty>
        ))}
        {report.status === 'COMPLETED' && report.fileUrl && (
          <ResourceProperty label="Archivo:">
            <LinkToModuleResource
              href={report.fileUrl}
              text="Descargar"
              isExternalPath
            />
          </ResourceProperty>
        )}
        {report.status === 'FAILED' && (
          <ResourceProperty label="Error:">
            {report.errorMessage || 'Ocurrió un error al generar el reporte'}
          </ResourceProperty>
        )}
      </>
    );
  }

  return (
    <Content
      breadcrumbs={breadcrumbs}
      title="Reporte de Itinerarios"
      content={content}
    />
  );
};

ItineraryCounterReport.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  match: matchPropTypes.isRequired,
  itineraryCounterReport: PropTypes.instanceOf(Immutable.Map).isRequired,
  loading: PropTypes.bool,
  dispatchGetItineraryCounterReport: PropTypes.func.isRequired,
  dispatchClearItineraryCounterReport: PropTypes.func.isRequired,
};

ItineraryCounterReport.defaultProps = {
  loading: false,
};

const mapDispatchToProps = {
  dispatchGetItineraryCounterReport: getItineraryCounterReport,
  dispatchClearItineraryCounterReport: clearItineraryCounterReport,
};

const mapStateToProps = ({ ItineraryUnit }, { match }) => ({
  breadcrumbs: [
    ...ItineraryUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Reportes de Itinerarios',
      href: ITINERARY_COUNTER_REPORT_PATH,
    },
    {
      text: `#${match.params.id}`,
      href: '',
    },
  ],
  itineraryCounterReport: ItineraryUnit.ItineraryCounterReport.getIn([
    'current',
    'content',
  ]),
  loading: !ItineraryUnit.ItineraryCounterReport.getIn([
    'current',
    'activity',
  ]).isEmpty(),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ItineraryCounterReport);
